import Acl from './acl.jsx'
import { logger } from './logging'
import storage from './localstorage.jsx'

const ACTIVE_TEAM_ID = 'activeTeamId'

export class TeamContext {
  constructor (app) {
    this.app = app
    this.activeTeam = null
    this.availableTeams = null
    this.activeUserTeam = null
  }

  async getTeamContext (user, teamId) {
    const [availableTeams, activeTeam, activeUserTeam, teamsUserIsInvitedTo] = await Promise.all([
      this.getTeams(user, { status: 'active' }),
      teamId ? this.getTeam(teamId) : null,
      teamId ? this.getUserTeam({ userId: user.id, teamId }) : null,
      this.getTeams(user, { status: 'invited' })
    ])
    return { availableTeams, activeTeam, activeUserTeam, teamsUserIsInvitedTo }
  }

  async getUserTeam ({ userId, teamId }) {
    const { data } = await this.app.service('/api/users/team').find({ query: { userId, teamId } })

    if (!data.length) {
      const error = new Error(`Could not fetch userteam for userId: ${userId} and teamId: ${teamId}`)
      error.name = 'UserTeamNotFound'
      throw error
    }

    return data[0]
  }

  getTeam (teamId) {
    this.app.service('/api/team').timeout = 15000
    return this.app.service('/api/team').get(teamId).then(team => {
      if (!team) {
        throw new Error('Team does not exist.')
      }

      const { active, profile, archived } = team

      if ((!active && profile.country !== 'US') || archived) {
        throw new Error('Team is archived or inactive.')
      }

      return team
    }).catch(e => {
      logger.error('teamContext.getTeam error:', e)
    })
  }

  /**
   * Get all the teams for the user based on user team filters.
   */
  getTeams (user, userTeamFilters = {}) {
    return this.app.service('/api/users/team')
      .find({ query: { userId: user.id, ...userTeamFilters } })
      .then(response => {
        const teamIds = response.data.map(userteam => userteam.teamId)
        this.app.service('/api/team').timeout = 10000
        return this.app.service('/api/team')
          .find({ query: { _id: teamIds, archived: false, active: true } })
          .then(response => response.data)
          .catch(e => {
            logger.error('teamContext.getTeams error:', e)
          })
      })
  }

  setTeams (teams) {
    logger.debug('Setting available teams for the user:', { teams })
    this.availableTeams = teams
  }

  setActiveTeam (team) {
    logger.debug('Setting active team for the user:', { team })
    this.activeTeam = team
    this.app.setTeamCountry(team.profile.country)
    this.setActiveTeamIdInStorage(team.id)
    Acl.setPlannerAllowed(team.subscription.isPlannerEnabled)
    Acl.setInternalExpensesAllowed(team.subscription.isInternalExpensesActive)
  }

  async setActiveUserTeam (userTeam) {
    logger.debug('Setting active userTeam for the user:', { userTeam })
    this.activeUserTeam = userTeam
    this.app.setLanguage(userTeam.language)
    this.app.setTimezone(userTeam.timezone)
    Acl.setUserRoles(userTeam.roleType, userTeam.roles)
    await this.createUserHistory({ userId: userTeam.userId, teamId: userTeam.teamId })
  }

  getActiveTeamIdFromStorage () {
    return storage.getItem(ACTIVE_TEAM_ID)
  }

  setActiveTeamIdInStorage (activeTeamId) {
    if (typeof activeTeamId !== 'string') {
      throw new Error(`Expected activeTeamId to be typeof string, received: ${typeof activeTeamId}`)
    }
    return storage.setItem(ACTIVE_TEAM_ID, activeTeamId)
  }

  async switchToTeamById ({ userId, teamId }) {
    const [team, userTeam] = await Promise.all([
      this.getTeam(teamId),
      this.getUserTeam({ teamId, userId })
    ])
    this.setActiveTeam(team)
    await this.setActiveUserTeam(userTeam)
    return { activeTeam: team, activeUserTeam: userTeam }
  }

  async createUserHistory ({ teamId, userId }) {
    const result = await this.app.service('/api/user/history').create({
      userId
    }, {
      query: {
        $client: { teamId: teamId }
      }
    })

    this.app.set('appUserHistoryId', result.id)
  }
}
